<template>
  <div class="traveller-form">
    <div
      v-for="(traveller, idx) in localTravellers"
      :key="idx"
      class="traveller-card"
    >
      <div class="traveller-card-top">
        <span class="traveller-badge">
          {{ traveller.isTraveling === false ? 'Policyholder (not travelling)' : `Traveller ${idx + 1}` }}
        </span>
        <div class="traveller-flags">
          <button
            v-if="!externalPolicyholder"
            type="button"
            class="policyholder-pill"
            :class="{ active: traveller.isMain, disabled: !canBePolicyholder(traveller) }"
            :disabled="!canBePolicyholder(traveller)"
            @click="setMain(idx)"
          >
            <v-icon x-small :color="traveller.isMain ? '#00AEEF' : '#94A3B8'" class="tw-mr-1">
              {{ traveller.isMain ? 'mdi-account-check' : 'mdi-account-outline' }}
            </v-icon>
            Policyholder
          </button>
          <button
            v-if="!fixedCount && localTravellers.length > 1"
            type="button"
            class="remove-traveller"
            aria-label="Remove traveller"
            @click="removeTraveller(idx)"
          >
            <v-icon size="16" color="#DC2626">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <div class="pa-form-fields">
        <div class="form-row">
          <div class="input-group">
            <label class="input-label">First name <span class="req">*</span></label>
            <v-text-field
              :value="traveller.firstName"
              outlined dense hide-details="auto"
              class="custom-input"
              maxlength="40"
              placeholder="e.g. John"
              :readonly="isIdentityLocked(traveller)"
              @input="updateField(idx, 'firstName', $event)"
            />
          </div>
          <div class="input-group">
            <label class="input-label">Surname <span class="req">*</span></label>
            <v-text-field
              :value="traveller.surname"
              outlined dense hide-details="auto"
              class="custom-input"
              maxlength="40"
              placeholder="e.g. Kamau"
              :readonly="isIdentityLocked(traveller)"
              @input="updateField(idx, 'surname', $event)"
            />
          </div>
        </div>

        <div class="form-row">
          <div class="input-group">
            <label class="input-label">Date of birth <span class="req">*</span></label>
            <v-text-field
              :value="traveller.dateOfBirth"
              type="date"
              :max="maxDob"
              :readonly="isDobLocked(traveller) || isIdentityLocked(traveller)"
              outlined dense hide-details="auto"
              class="custom-input"
              @input="updateField(idx, 'dateOfBirth', $event)"
            />
          </div>
          <div class="input-group">
            <label class="input-label">Passport <span class="req">*</span></label>
            <v-text-field
              :value="traveller.passport"
              outlined dense
              class="custom-input"
              :maxlength="TRAVEL_PASSPORT_MAX_LEN"
              placeholder="e.g. A1234567"
              :error-messages="passportFieldError(traveller)"
              :error="passportFieldError(traveller).length > 0"
              @input="updateField(idx, 'passport', $event)"
            />
          </div>
        </div>
      </div>
    </div>

    <button
      v-if="!fixedCount && localTravellers.length < maxTravellers"
      type="button"
      class="add-traveller-btn"
      @click="addTraveller"
    >
      <v-icon x-small color="#1E4276" class="tw-mr-1">mdi-plus</v-icon>
      Add traveller
    </button>
  </div>
</template>

<script>
import {
  isTravelAdult,
  normalizeTravelPassport,
  reconcileTravelPolicyholder,
  travelPassportError,
  TRAVEL_PASSPORT_MAX_LEN,
} from '@/utils/travelPricing';

function emptyTraveller(isMain = false) {
  return {
    firstName: '',
    surname: '',
    dateOfBirth: '',
    passport: '',
    isMain,
    isTraveling: true,
  };
}

export default {
  name: 'TravelTravellerForm',
  props: {
    travellers: { type: Array, default: () => [emptyTraveller(true)] },
    maxTravellers: { type: Number, default: 10 },
    /** When true, count is fixed (set at quote) — no add/remove. */
    fixedCount: { type: Boolean, default: false },
    /** Lock DOB fields that were priced at quote step. */
    lockQuoteDobs: { type: Boolean, default: false },
    /** Adult policyholder is captured separately (e.g. customer booking for someone else). */
    externalPolicyholder: { type: Boolean, default: false },
  },
  data() {
    return {
      TRAVEL_PASSPORT_MAX_LEN,
      localTravellers: [],
      maxDob: new Date().toISOString().slice(0, 10),
    };
  },
  watch: {
    travellers: {
      immediate: true,
      deep: true,
      handler(val) {
        this.syncFromProps(val);
      },
    },
    maxTravellers() {
      if (this.fixedCount) this.syncFromProps(this.travellers);
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.localTravellers.length) this.emitChange();
    });
  },
  methods: {
    isDobLocked(traveller) {
      return this.lockQuoteDobs && !!traveller.dobFromQuote && !!traveller.dateOfBirth;
    },
    isIdentityLocked(traveller) {
      return !!traveller.identityFromIprs;
    },
    syncFromProps(val) {
      const target = this.fixedCount
        ? Math.min(Math.max(this.maxTravellers || 1, 1), 10)
        : null;
      let rows = (val && val.length)
        ? JSON.parse(JSON.stringify(val))
        : [emptyTraveller(true)];
      if (this.fixedCount && target) {
        if (rows.length > target) rows = rows.slice(0, target);
        if (!this.externalPolicyholder && !rows.some((r) => r.isMain) && rows.length) {
          rows[0].isMain = true;
        }
      }
      if (this.externalPolicyholder) {
        rows = rows.map((t) => ({ ...t, isMain: false, isTraveling: true }));
      } else {
        rows = reconcileTravelPolicyholder(rows);
      }
      this.localTravellers = rows;
    },
    canBePolicyholder(traveller) {
      if (!traveller?.dateOfBirth) return true;
      return isTravelAdult(traveller.dateOfBirth);
    },
    passportFieldError(traveller) {
      if (!traveller?.passport) return [];
      const err = travelPassportError(traveller.passport);
      return err ? [err] : [];
    },
    emitChange() {
      this.$emit('update:travellers', JSON.parse(JSON.stringify(this.localTravellers)));
    },
    updateField(idx, field, value) {
      const row = this.localTravellers[idx];
      if (row?.identityFromIprs && ['firstName', 'surname', 'dateOfBirth'].includes(field)) {
        return;
      }
      const nextValue = field === 'passport' ? normalizeTravelPassport(value) : value;
      this.localTravellers[idx][field] = nextValue;
      if (field === 'dateOfBirth' && !this.externalPolicyholder) {
        this.localTravellers = reconcileTravelPolicyholder(this.localTravellers, { preferIndex: idx });
      }
      this.emitChange();
    },
    setMain(idx) {
      const row = this.localTravellers[idx];
      if (row.dateOfBirth && !isTravelAdult(row.dateOfBirth)) {
        this.$emit('policyholder-error', 'Policyholder must be at least 18 years old.');
        return;
      }
      this.localTravellers = this.localTravellers.map((traveller, i) => ({
        ...traveller,
        isMain: i === idx,
      }));
      this.emitChange();
    },
    addTraveller() {
      if (this.localTravellers.length >= this.maxTravellers) return;
      this.localTravellers.push(emptyTraveller(false));
      this.emitChange();
    },
    removeTraveller(idx) {
      const wasMain = this.localTravellers[idx].isMain;
      this.localTravellers.splice(idx, 1);
      if (wasMain && this.localTravellers.length) {
        this.localTravellers = reconcileTravelPolicyholder(this.localTravellers);
      }
      this.emitChange();
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/agent-pa-form.scss';

.traveller-form { display: flex; flex-direction: column; gap: 0; }

.traveller-card {
  border: 1px solid #E8EDF2;
  border-radius: 12px;
  padding: 16px 18px 6px;
  margin-bottom: 14px;
  background: linear-gradient(180deg, #FAFCFE 0%, #FFFFFF 100%);
  box-shadow: 0 1px 3px rgba(30, 66, 118, 0.04);
}

.traveller-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
}

.traveller-badge {
  font-size: 0.72rem;
  font-weight: 800;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #E0F2FE;
  padding: 4px 10px;
  border-radius: 20px;
}

.traveller-flags {
  display: flex;
  align-items: center;
  gap: 12px;
}

.policyholder-pill {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.76rem;
  font-weight: 700;
  color: #64748B;
  cursor: pointer;
  margin: 0;
  padding: 6px 12px;
  border-radius: 20px;
  border: 1.5px solid #E2E8F0;
  background: #F8FAFC;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.policyholder-pill.active {
  border-color: #00AEEF;
  background: #E0F2FE;
  color: #1E4276;
}

.policyholder-pill.disabled,
.policyholder-pill:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.remove-traveller {
  border: none;
  background: #FEE2E2;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.add-traveller-btn {
  display: inline-flex;
  align-items: center;
  border: 1.5px dashed #CBD5E1;
  background: white;
  color: #1E4276;
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.82rem;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.add-traveller-btn:hover {
  border-color: #00AEEF;
  color: #00AEEF;
}

</style>
